import { useState, useMemo, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaFilter,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
  FaSlidersH,
  FaRupeeSign,
  FaPlane,
  FaClock,
  FaCheckCircle,
  FaUtensils,
  FaSuitcase,
  FaExchangeAlt,
  FaSearch,
  FaUndo,
} from "react-icons/fa";
import { calculateTotalDuration } from "../../utils/flightHelpers";

/** Normalize segments to array (API may return array or object). Safe for one-way and round trip. */
const toSegmentArray = (seg) => {
  if (seg == null) return [];
  if (Array.isArray(seg)) return seg;
  if (typeof seg === "object") return Object.values(seg).filter((s) => s && (s.flightNo || s.airCode));
  return [];
};

/** Get onward segments from a flight (one-way or round trip). */
const getOnwardSegments = (flight) => {
  const raw = flight?.Flights?.Onward ?? flight?.onwardFlight?.Flights?.Onward ?? flight?.flights?.Onward;
  return toSegmentArray(raw);
};

/** Get return segments from a flight (round trip only). */
const getReturnSegments = (flight) => {
  const raw = flight?.Flights?.Return ?? flight?.returnFlight?.Flights?.Onward ?? flight?.returnFlight?.Flights?.Return;
  return toSegmentArray(raw);
};

/** All segments for a flight (onward + return for round trip) for extracting airlines/terminals. */
const getAllSegments = (flight) => {
  const onward = getOnwardSegments(flight);
  const returnSegs = flight?.isRoundTrip ? getReturnSegments(flight) : [];
  return [...onward, ...returnSegs];
};

/** Price for filter: total for round trip, else single fare. */
const getFlightPrice = (flight) => {
  if (flight?.isRoundTrip && (flight?.totalPrice != null)) {
    return parseInt(flight.totalPrice, 10) || 0;
  }
  const fare = flight?.Fare ?? flight?.onwardFlight?.Fare ?? flight?.fare ?? {};
  return parseInt(fare?.total?.total ?? fare?.total ?? 0, 10) || 0;
};

/** Duration in minutes: total for round trip, else single leg. */
const getFlightDurationMinutes = (flight) => {
  if (flight?.isRoundTrip && (flight?.totalDuration != null)) {
    return Number(flight.totalDuration) || 0;
  }
  const onward = flight?.Flights?.Onward ?? flight?.onwardFlight?.Flights?.Onward;
  return calculateTotalDuration(onward);
};

const AdvancedFlightFilters = ({
  flights,
  filteredFlights,
  setFilteredFlights,
  onFilterChange,
}) => {
  const [isOpen, setIsOpen] = useState(true); // Open by default for better UX
  const [searchTerm, setSearchTerm] = useState("");

  // Advanced filter state
  const [filters, setFilters] = useState({
    // Basic Filters
    fareType: "A", // A=All, S=Student, C=Senior Citizen, D=Defence, SME=SME, P=Promo
    cabin: "E", // E=Economy, P=Premium, B=Business, F=First
    stops: null, // null=All, 0=Direct, 1=1 Stop, 2=2+ Stops
    airlines: [], // Array of selected airline codes
    refundable: null, // null=All, "P"=Refundable, "N"=Non-Refundable
    
    // Price Filters
    priceRange: [0, 100000],
    priceSort: null, // null=No sort, "low"=Low to High, "high"=High to Low
    
    // Time Filters
    departureTime: null, // null=All, "morning", "afternoon", "evening", "night"
    arrivalTime: null, // null=All, "morning", "afternoon", "evening", "night"
    durationRange: [0, 1440], // Duration in minutes (0 to 24 hours)
    
    // V2 Enhanced Filters
    freeMeal: null, // null=All, true=With Free Meal, false=Without
    lcc: null, // null=All, true=Low Cost Carrier, false=Full Service
    terminals: [], // Array of terminal codes to filter
    operatingAirlines: [], // Array of operating airline codes
    
    // Advanced Options
    showOnlyDirect: false,
    showOnlyRefundable: false,
    showOnlyWithMeals: false,
  });

  // Extract unique values from flights for dynamic filter options (one-way + round trip safe)
  const filterOptions = useMemo(() => {
    const airlinesSet = new Set();
    const terminalsSet = new Set();
    const operatingAirlinesSet = new Set();
    const prices = [];
    const durations = [];

    flights.forEach((flight) => {
      const segments = getAllSegments(flight);
      segments.forEach((segment) => {
        if (segment?.airCode) {
          airlinesSet.add(JSON.stringify({
            code: segment.airCode,
            name: segment.airName || segment.airCode,
          }));
        }
        if (segment?.airCodeOp) {
          operatingAirlinesSet.add(JSON.stringify({
            code: segment.airCodeOp,
            name: segment.airNameOp || segment.airCodeOp,
          }));
        }
        if (segment?.depTer) terminalsSet.add(segment.depTer);
        if (segment?.arrTer) terminalsSet.add(segment.arrTer);
      });

      const price = getFlightPrice(flight);
      if (price > 0) prices.push(price);

      const duration = getFlightDurationMinutes(flight);
      if (duration > 0) durations.push(duration);
    });

    const airlines = Array.from(airlinesSet).map((item) => JSON.parse(item));
    const operatingAirlines = Array.from(operatingAirlinesSet).map((item) => JSON.parse(item));
    const terminals = Array.from(terminalsSet);

    return {
      airlines: airlines.sort((a, b) => (a.name || "").localeCompare(b.name || "")),
      operatingAirlines: operatingAirlines.sort((a, b) => (a.name || "").localeCompare(b.name || "")),
      terminals: terminals.sort(),
      priceRange: {
        min: prices.length > 0 ? Math.min(...prices) : 0,
        max: prices.length > 0 ? Math.max(...prices) : 100000,
      },
      durationRange: {
        min: durations.length > 0 ? Math.min(...durations) : 0,
        max: durations.length > 0 ? Math.max(...durations) : 1440,
      },
    };
  }, [flights]);

  // Advanced filtering logic with comprehensive checks
  const applyFilters = useCallback((newFilters) => {
    let filtered = [...flights];

    // 1. Search Term Filter (if provided)
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((flight) => {
        const segments = getAllSegments(flight);
        const matchSegment = (seg) => {
          const airlineName = (seg?.airName || "").toLowerCase();
          const flightNo = (seg?.flightNo || "").toLowerCase();
          const depCity = ((seg?.depCName || seg?.depAName) || "").toLowerCase();
          const arrCity = ((seg?.arrCName || seg?.arrAName) || "").toLowerCase();
          return (
            airlineName.includes(searchLower) ||
            flightNo.includes(searchLower) ||
            depCity.includes(searchLower) ||
            arrCity.includes(searchLower)
          );
        };
        return segments.some(matchSegment);
      });
    }

    // 2. Fare Type Filter (Enhanced for V2)
    if (newFilters.fareType !== "A") {
      filtered = filtered.filter((flight) => {
        const fare = 
          flight?.Fare || 
          flight?.onwardFlight?.Fare || 
          flight?.fare || 
          {};
        const fareTypeDesc = (fare?.fareTypeDesc || "").toLowerCase();
        const popMsg = (fare?.popMsg || "").toLowerCase();
        const fareTypeInd = fare?.fareTypeInd || "";

        switch (newFilters.fareType) {
          case "S": // Student
            return (
              fareTypeDesc.includes("student") ||
              popMsg.includes("student") ||
              fareTypeInd === "2"
            );
          case "C": // Senior Citizen
            return (
              fareTypeDesc.includes("senior") ||
              fareTypeDesc.includes("citizen") ||
              popMsg.includes("senior") ||
              fareTypeInd === "3"
            );
          case "D": // Defence
            return (
              fareTypeDesc.includes("defence") ||
              fareTypeDesc.includes("defense") ||
              popMsg.includes("defence") ||
              fareTypeInd === "4"
            );
          case "SME": // SME
            return (
              fareTypeDesc.includes("sme") ||
              popMsg.includes("sme") ||
              fareTypeInd === "5"
            );
          case "P": // Promo
            return (
              fareTypeDesc.includes("promo") ||
              fareTypeDesc.includes("offer") ||
              popMsg.includes("promo") ||
              popMsg.includes("offer") ||
              fareTypeInd === "6"
            );
          default:
            return true;
        }
      });
    }

    // 3. Cabin Filter
    if (newFilters.cabin !== "E") {
      filtered = filtered.filter((flight) => {
        const segments = getOnwardSegments(flight);
        if (segments.length === 0) return false;
        const cabin = segments[0]?.cabin || flight?.cabin;
        return cabin === newFilters.cabin;
      });
    }

    // 4. Stops Filter (one-way: leg stops; round trip: max of onward & return stops)
    if (newFilters.stops !== null) {
      filtered = filtered.filter((flight) => {
        const onwardSegs = getOnwardSegments(flight);
        const returnSegs = getReturnSegments(flight);
        const onwardStops = onwardSegs.length > 1 ? onwardSegs.length - 1 : 0;
        const returnStops = returnSegs.length > 1 ? returnSegs.length - 1 : 0;
        const actualStops = Math.max(onwardStops, returnStops);
        if (newFilters.stops === 0) return actualStops === 0;
        if (newFilters.stops === 1) return actualStops === 1;
        if (newFilters.stops === 2) return actualStops >= 2;
        return true;
      });
    }

    // 5. Airlines Filter (Multiple selection)
    if (newFilters.airlines.length > 0) {
      filtered = filtered.filter((flight) => {
        const segments = getAllSegments(flight);
        return segments.some((seg) => newFilters.airlines.includes(seg?.airCode));
      });
    }

    // 6. Operating Airlines Filter (V2)
    if (newFilters.operatingAirlines.length > 0) {
      filtered = filtered.filter((flight) => {
        const segments = getAllSegments(flight);
        return segments.some((seg) =>
          newFilters.operatingAirlines.includes(seg?.airCodeOp || seg?.airCode)
        );
      });
    }

    // 7. Price Range Filter (total for round trip, single for one-way)
    filtered = filtered.filter((flight) => {
      const price = getFlightPrice(flight);
      return price >= newFilters.priceRange[0] && price <= newFilters.priceRange[1];
    });

    // 8. Refundable Filter
    if (newFilters.refundable !== null) {
      filtered = filtered.filter((flight) => {
        const fare = flight?.Fare ?? flight?.onwardFlight?.Fare ?? flight?.fare ?? {};
        const refundType = fare?.refundType;
        if (!refundType) return newFilters.refundable === null;
        return refundType === newFilters.refundable;
      });
    }

    // 9. Departure Time Filter (onward leg first segment)
    if (newFilters.departureTime !== null) {
      filtered = filtered.filter((flight) => {
        const segments = getOnwardSegments(flight);
        if (segments.length === 0) return false;
        const depTime = segments[0]?.depDate || segments[0]?.depTime || "";
        const hour = extractHour(depTime);
        return getTimeCategory(hour) === newFilters.departureTime;
      });
    }

    // 10. Arrival Time Filter (onward leg last segment)
    if (newFilters.arrivalTime !== null) {
      filtered = filtered.filter((flight) => {
        const segments = getOnwardSegments(flight);
        if (segments.length === 0) return false;
        const lastSegment = segments[segments.length - 1];
        const arrTime = lastSegment?.arrDate || lastSegment?.arrTime || "";
        const hour = extractHour(arrTime);
        return getTimeCategory(hour) === newFilters.arrivalTime;
      });
    }

    // 11. Duration Range Filter (total for round trip, single leg for one-way)
    filtered = filtered.filter((flight) => {
      const duration = getFlightDurationMinutes(flight);
      return (
        duration >= newFilters.durationRange[0] &&
        duration <= newFilters.durationRange[1]
      );
    });

    // 12. Free Meal Filter (V2) – check onward fare
    if (newFilters.freeMeal !== null) {
      filtered = filtered.filter((flight) => {
        const fare = flight?.Fare ?? flight?.onwardFlight?.Fare ?? flight?.fare ?? {};
        const validation = fare?.validation || {};
        const hasFreeMeal = validation?.freeMeal === "1" || validation?.freeMeal === 1;
        return newFilters.freeMeal ? hasFreeMeal : !hasFreeMeal;
      });
    }

    // 13. LCC Filter (V2)
    if (newFilters.lcc !== null) {
      filtered = filtered.filter((flight) => {
        const fare = flight?.Fare ?? flight?.onwardFlight?.Fare ?? flight?.fare ?? {};
        const validation = fare?.validation || {};
        const isLCC = validation?.lcc === "1" || validation?.lcc === 1;
        return newFilters.lcc ? isLCC : !isLCC;
      });
    }

    // 14. Terminals Filter (V2) – any segment matches
    if (newFilters.terminals.length > 0) {
      filtered = filtered.filter((flight) => {
        const segments = getAllSegments(flight);
        return segments.some(
          (seg) =>
            newFilters.terminals.includes(seg?.depTer) ||
            newFilters.terminals.includes(seg?.arrTer)
        );
      });
    }

    // 15. Quick Filters – Direct = both legs non-stop for round trip
    if (newFilters.showOnlyDirect) {
      filtered = filtered.filter((flight) => {
        const onwardSegs = getOnwardSegments(flight);
        const returnSegs = getReturnSegments(flight);
        return onwardSegs.length <= 1 && returnSegs.length <= 1;
      });
    }

    if (newFilters.showOnlyRefundable) {
      filtered = filtered.filter((flight) => {
        const fare = 
          flight?.Fare || 
          flight?.onwardFlight?.Fare || 
          flight?.fare || 
          {};
        return fare?.refundType === "P";
      });
    }

    if (newFilters.showOnlyWithMeals) {
      filtered = filtered.filter((flight) => {
        const fare = 
          flight?.Fare || 
          flight?.onwardFlight?.Fare || 
          flight?.fare || 
          {};
        const validation = fare?.validation || {};
        return validation?.freeMeal === "1" || validation?.freeMeal === 1;
      });
    }

    // 16. Price Sorting (if enabled) – total for round trip
    if (newFilters.priceSort) {
      filtered.sort((a, b) => {
        const priceA = getFlightPrice(a);
        const priceB = getFlightPrice(b);
        return newFilters.priceSort === "low" ? priceA - priceB : priceB - priceA;
      });
    }

    setFilteredFlights(filtered);
    if (onFilterChange) onFilterChange(filtered);
  }, [flights, searchTerm, setFilteredFlights, onFilterChange]);

  // Helper functions
  const extractHour = (timeString) => {
    if (!timeString) return 0;
    // Handle formats: "20260126100000" or "10:00"
    if (timeString.length >= 10) {
      return parseInt(timeString.substring(8, 10)) || 0;
    }
    if (timeString.includes(":")) {
      return parseInt(timeString.split(":")[0]) || 0;
    }
    return 0;
  };

  const getTimeCategory = (hour) => {
    if (hour >= 6 && hour < 12) return "morning";
    if (hour >= 12 && hour < 18) return "afternoon";
    if (hour >= 18 && hour < 24) return "evening";
    return "night";
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  // Handle filter changes
  const handleFilterChange = useCallback((key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    applyFilters(newFilters);
  }, [filters, applyFilters]);

  // Handle search term change
  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
    // Re-apply filters with new search term
    applyFilters(filters);
  }, [filters, applyFilters]);

  // Reset all filters
  const resetFilters = useCallback(() => {
    const defaultFilters = {
      fareType: "A",
      cabin: "E",
      stops: null,
      airlines: [],
      refundable: null,
      priceRange: [filterOptions.priceRange.min, filterOptions.priceRange.max],
      priceSort: null,
      departureTime: null,
      arrivalTime: null,
      durationRange: [filterOptions.durationRange.min, filterOptions.durationRange.max],
      freeMeal: null,
      lcc: null,
      terminals: [],
      operatingAirlines: [],
      showOnlyDirect: false,
      showOnlyRefundable: false,
      showOnlyWithMeals: false,
    };
    setFilters(defaultFilters);
    setSearchTerm("");
    applyFilters(defaultFilters);
  }, [filterOptions, applyFilters]);

  // Calculate active filters count
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.fareType !== "A") count++;
    if (filters.cabin !== "E") count++;
    if (filters.stops !== null) count++;
    if (filters.airlines.length > 0) count++;
    if (filters.operatingAirlines.length > 0) count++;
    if (filters.refundable !== null) count++;
    if (filters.priceRange[0] !== filterOptions.priceRange.min || 
        filters.priceRange[1] !== filterOptions.priceRange.max) count++;
    if (filters.priceSort !== null) count++;
    if (filters.departureTime !== null) count++;
    if (filters.arrivalTime !== null) count++;
    if (filters.durationRange[0] !== filterOptions.durationRange.min || 
        filters.durationRange[1] !== filterOptions.durationRange.max) count++;
    if (filters.freeMeal !== null) count++;
    if (filters.lcc !== null) count++;
    if (filters.terminals.length > 0) count++;
    if (filters.showOnlyDirect) count++;
    if (filters.showOnlyRefundable) count++;
    if (filters.showOnlyWithMeals) count++;
    if (searchTerm.trim()) count++;
    return count;
  }, [filters, filterOptions, searchTerm]);

  // Initialize filters when flights change (only update ranges, don't re-apply)
  useEffect(() => {
    if (filterOptions.priceRange.min !== filters.priceRange[0] || 
        filterOptions.priceRange.max !== filters.priceRange[1] ||
        filterOptions.durationRange.min !== filters.durationRange[0] ||
        filterOptions.durationRange.max !== filters.durationRange[1]) {
      setFilters(prev => ({
        ...prev,
        priceRange: [filterOptions.priceRange.min, filterOptions.priceRange.max],
        durationRange: [filterOptions.durationRange.min, filterOptions.durationRange.max],
      }));
    }
  }, [filterOptions.priceRange.min, filterOptions.priceRange.max, filterOptions.durationRange.min, filterOptions.durationRange.max]);

  return (
    <div className="mb-6">
      {/* Filter Header - Enhanced with gradient and better colors */}
      <motion.div 
        className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 rounded-xl shadow-lg border border-blue-100 p-4 mb-4"
        whileHover={{ scale: 1.01 }}
        transition={{ duration: 0.2 }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg shadow-md">
              <FaSlidersH className="text-white text-lg" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-lg">Advanced Filters</h3>
              <p className="text-xs text-gray-500">Refine your search</p>
            </div>
            {activeFiltersCount > 0 && (
              <motion.span 
                className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-md"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 500 }}
              >
                {activeFiltersCount} active
              </motion.span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {activeFiltersCount > 0 && (
              <motion.button
                onClick={resetFilters}
                className="px-4 py-2 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-semibold rounded-lg hover:from-red-600 hover:to-pink-600 transition-all duration-200 flex items-center space-x-1 shadow-md"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                title="Reset all filters"
              >
                <FaUndo className="text-xs" />
                <span>Reset</span>
              </motion.button>
            )}
            <motion.button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              {isOpen ? (
                <FaChevronUp className="text-lg" />
              ) : (
                <FaChevronDown className="text-lg" />
              )}
            </motion.button>
          </div>
        </div>

        {/* Quick Search - Enhanced */}
        <motion.div 
          className="mb-3"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-500" />
            <input
              type="text"
              placeholder="Search by airline, flight number, city..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 border-2 border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm transition-all duration-200"
            />
            {searchTerm && (
              <motion.button
                onClick={() => handleSearchChange("")}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors"
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaTimes />
              </motion.button>
            )}
          </div>
        </motion.div>

        {/* Quick filter summary when collapsed */}
        {!isOpen && activeFiltersCount > 0 && (
          <div className="text-sm text-gray-600">
            {activeFiltersCount} filter{activeFiltersCount > 1 ? "s" : ""} active
          </div>
        )}
      </motion.div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="bg-white rounded-xl shadow-lg border border-gray-100 p-5 max-h-[calc(100vh-200px)] overflow-y-auto"
          >
            <div className="space-y-5">
              {/* Section: Basic Filters */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
              >
                <h4 className="text-sm font-bold text-gray-800 mb-4 pb-2 border-b-2 border-blue-200 flex items-center">
                  <div className="w-1 h-5 bg-gradient-to-b from-blue-500 to-indigo-600 rounded-full mr-2"></div>
                  Basic Filters
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Fare Type */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Fare Type
                    </label>
                    <select
                      value={filters.fareType}
                      onChange={(e) => handleFilterChange("fareType", e.target.value)}
                      className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition-all duration-200 hover:border-blue-300 cursor-pointer"
                    >
                      <option value="A">All Fares</option>
                      <option value="S">Student Fare</option>
                      <option value="C">Senior Citizen</option>
                      <option value="D">Defence</option>
                      <option value="SME">SME</option>
                      <option value="P">Promo/Offer</option>
                    </select>
                  </div>

                  {/* Cabin Class */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Cabin Class
                    </label>
                    <select
                      value={filters.cabin}
                      onChange={(e) => handleFilterChange("cabin", e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="E">Economy</option>
                      <option value="P">Premium Economy</option>
                      <option value="B">Business</option>
                      <option value="F">First Class</option>
                    </select>
                  </div>

                  {/* Stops */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Stops
                    </label>
                    <select
                      value={filters.stops === null ? "all" : filters.stops}
                      onChange={(e) =>
                        handleFilterChange("stops", e.target.value === "all" ? null : parseInt(e.target.value))
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">All</option>
                      <option value="0">Direct (Non-stop)</option>
                      <option value="1">1 Stop</option>
                      <option value="2">2+ Stops</option>
                    </select>
                  </div>

                  {/* Refundable */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Refund Policy
                    </label>
                    <select
                      value={filters.refundable === null ? "all" : filters.refundable}
                      onChange={(e) =>
                        handleFilterChange("refundable", e.target.value === "all" ? null : e.target.value)
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">All</option>
                      <option value="P">Refundable</option>
                      <option value="N">Non-Refundable</option>
                    </select>
                  </div>
                </div>
              </motion.div>

              {/* Section: Price Filters */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <h4 className="text-sm font-bold text-gray-800 mb-4 pb-2 border-b-2 border-green-200 flex items-center">
                  <div className="w-1 h-5 bg-gradient-to-b from-green-500 to-emerald-600 rounded-full mr-2"></div>
                  Price Filters
                </h4>
                <div className="space-y-4">
                  {/* Price Range */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Price Range: ₹{filters.priceRange[0].toLocaleString()} - ₹{filters.priceRange[1].toLocaleString()}
                    </label>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-4">
                        <input
                          type="number"
                          min={filterOptions.priceRange.min}
                          max={filterOptions.priceRange.max}
                          value={filters.priceRange[0]}
                          onChange={(e) => {
                            const min = Math.max(
                              filterOptions.priceRange.min,
                              Math.min(parseInt(e.target.value) || 0, filters.priceRange[1])
                            );
                            handleFilterChange("priceRange", [min, filters.priceRange[1]]);
                          }}
                          className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="Min"
                        />
                        <span className="text-gray-500">to</span>
                        <input
                          type="number"
                          min={filterOptions.priceRange.min}
                          max={filterOptions.priceRange.max}
                          value={filters.priceRange[1]}
                          onChange={(e) => {
                            const max = Math.min(
                              filterOptions.priceRange.max,
                              Math.max(parseInt(e.target.value) || filterOptions.priceRange.max, filters.priceRange[0])
                            );
                            handleFilterChange("priceRange", [filters.priceRange[0], max]);
                          }}
                          className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="Max"
                        />
                      </div>
                      <div className="flex space-x-2">
                        <input
                          type="range"
                          min={filterOptions.priceRange.min}
                          max={filterOptions.priceRange.max}
                          value={filters.priceRange[0]}
                          onChange={(e) =>
                            handleFilterChange("priceRange", [
                              parseInt(e.target.value),
                              filters.priceRange[1],
                            ])
                          }
                          className="flex-1"
                        />
                        <input
                          type="range"
                          min={filterOptions.priceRange.min}
                          max={filterOptions.priceRange.max}
                          value={filters.priceRange[1]}
                          onChange={(e) =>
                            handleFilterChange("priceRange", [
                              filters.priceRange[0],
                              parseInt(e.target.value),
                            ])
                          }
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Price Sort */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Sort by Price
                    </label>
                    <select
                      value={filters.priceSort || "none"}
                      onChange={(e) =>
                        handleFilterChange("priceSort", e.target.value === "none" ? null : e.target.value)
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="none">No Sorting</option>
                      <option value="low">Price: Low to High</option>
                      <option value="high">Price: High to Low</option>
                    </select>
                  </div>
                </div>
              </motion.div>

              {/* Section: Time Filters */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <h4 className="text-sm font-bold text-gray-800 mb-4 pb-2 border-b-2 border-purple-200 flex items-center">
                  <div className="w-1 h-5 bg-gradient-to-b from-purple-500 to-pink-600 rounded-full mr-2"></div>
                  Time Filters
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Departure Time */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Departure Time
                    </label>
                    <select
                      value={filters.departureTime || "all"}
                      onChange={(e) =>
                        handleFilterChange("departureTime", e.target.value === "all" ? null : e.target.value)
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">All Times</option>
                      <option value="morning">Morning (6 AM - 12 PM)</option>
                      <option value="afternoon">Afternoon (12 PM - 6 PM)</option>
                      <option value="evening">Evening (6 PM - 12 AM)</option>
                      <option value="night">Night (12 AM - 6 AM)</option>
                    </select>
                  </div>

                  {/* Arrival Time */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Arrival Time
                    </label>
                    <select
                      value={filters.arrivalTime || "all"}
                      onChange={(e) =>
                        handleFilterChange("arrivalTime", e.target.value === "all" ? null : e.target.value)
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">All Times</option>
                      <option value="morning">Morning (6 AM - 12 PM)</option>
                      <option value="afternoon">Afternoon (12 PM - 6 PM)</option>
                      <option value="evening">Evening (6 PM - 12 AM)</option>
                      <option value="night">Night (12 AM - 6 AM)</option>
                    </select>
                  </div>

                  {/* Duration Range */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Flight Duration: {formatDuration(filters.durationRange[0])} - {formatDuration(filters.durationRange[1])}
                    </label>
                    <div className="flex space-x-3">
                      <input
                        type="range"
                        min={filterOptions.durationRange.min}
                        max={filterOptions.durationRange.max}
                        value={filters.durationRange[0]}
                        onChange={(e) =>
                          handleFilterChange("durationRange", [
                            parseInt(e.target.value),
                            filters.durationRange[1],
                          ])
                        }
                        className="flex-1"
                      />
                      <input
                        type="range"
                        min={filterOptions.durationRange.min}
                        max={filterOptions.durationRange.max}
                        value={filters.durationRange[1]}
                        onChange={(e) =>
                          handleFilterChange("durationRange", [
                            filters.durationRange[0],
                            parseInt(e.target.value),
                          ])
                        }
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Section: Airlines */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
              >
                <h4 className="text-sm font-bold text-gray-800 mb-4 pb-2 border-b-2 border-orange-200 flex items-center">
                  <div className="w-1 h-5 bg-gradient-to-b from-orange-500 to-red-600 rounded-full mr-2"></div>
                  Airlines
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Marketing Airlines */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Marketing Airlines ({filters.airlines.length} selected)
                    </label>
                    <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-2">
                      {filterOptions.airlines.length === 0 ? (
                        <p className="text-xs text-gray-500 text-center py-2">No airlines found</p>
                      ) : (
                        filterOptions.airlines.map((airline) => (
                          <label
                            key={airline.code}
                            className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={filters.airlines.includes(airline.code)}
                              onChange={(e) => {
                                const newAirlines = e.target.checked
                                  ? [...filters.airlines, airline.code]
                                  : filters.airlines.filter((a) => a !== airline.code);
                                handleFilterChange("airlines", newAirlines);
                              }}
                              className="rounded text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">{airline.name}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Operating Airlines (V2) */}
                  {filterOptions.operatingAirlines.length > 0 && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Operating Airlines ({filters.operatingAirlines.length} selected)
                      </label>
                      <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-2">
                        {filterOptions.operatingAirlines.map((airline) => (
                          <label
                            key={airline.code}
                            className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={filters.operatingAirlines.includes(airline.code)}
                              onChange={(e) => {
                                const newAirlines = e.target.checked
                                  ? [...filters.operatingAirlines, airline.code]
                                  : filters.operatingAirlines.filter((a) => a !== airline.code);
                                handleFilterChange("operatingAirlines", newAirlines);
                              }}
                              className="rounded text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">{airline.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Section: V2 Enhanced Filters */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
              >
                <h4 className="text-sm font-bold text-gray-800 mb-4 pb-2 border-b-2 border-indigo-200 flex items-center">
                  <div className="w-1 h-5 bg-gradient-to-b from-indigo-500 to-blue-600 rounded-full mr-2"></div>
                  V2 Enhanced Filters
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Free Meal */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Free Meal
                    </label>
                    <select
                      value={filters.freeMeal === null ? "all" : filters.freeMeal ? "yes" : "no"}
                      onChange={(e) => {
                        const value = e.target.value === "all" ? null : e.target.value === "yes";
                        handleFilterChange("freeMeal", value);
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">All</option>
                      <option value="yes">With Free Meal</option>
                      <option value="no">Without Free Meal</option>
                    </select>
                  </div>

                  {/* LCC */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Carrier Type
                    </label>
                    <select
                      value={filters.lcc === null ? "all" : filters.lcc ? "lcc" : "full"}
                      onChange={(e) => {
                        const value = e.target.value === "all" ? null : e.target.value === "lcc";
                        handleFilterChange("lcc", value);
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">All</option>
                      <option value="lcc">Low Cost Carrier</option>
                      <option value="full">Full Service Carrier</option>
                    </select>
                  </div>

                  {/* Terminals */}
                  {filterOptions.terminals.length > 0 && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Terminals ({filters.terminals.length} selected)
                      </label>
                      <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-lg p-2">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {filterOptions.terminals.map((terminal) => (
                            <label
                              key={terminal}
                              className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={filters.terminals.includes(terminal)}
                                onChange={(e) => {
                                  const newTerminals = e.target.checked
                                    ? [...filters.terminals, terminal]
                                    : filters.terminals.filter((t) => t !== terminal);
                                  handleFilterChange("terminals", newTerminals);
                                }}
                                className="rounded text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700">{terminal}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Section: Quick Filters */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 }}
              >
                <h4 className="text-sm font-bold text-gray-800 mb-4 pb-2 border-b-2 border-teal-200 flex items-center">
                  <div className="w-1 h-5 bg-gradient-to-b from-teal-500 to-cyan-600 rounded-full mr-2"></div>
                  Quick Filters
                </h4>
                <div className="flex flex-wrap gap-3">
                  <motion.label 
                    className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg cursor-pointer transition-all duration-200 ${
                      filters.showOnlyDirect 
                        ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md" 
                        : "border-2 border-gray-200 hover:border-blue-300 bg-white"
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <input
                      type="checkbox"
                      checked={filters.showOnlyDirect}
                      onChange={(e) => handleFilterChange("showOnlyDirect", e.target.checked)}
                      className="rounded text-blue-600 focus:ring-blue-500"
                    />
                    <span className={`text-sm font-medium ${filters.showOnlyDirect ? "text-white" : "text-gray-700"}`}>
                      Direct Flights Only
                    </span>
                  </motion.label>
                  <motion.label 
                    className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg cursor-pointer transition-all duration-200 ${
                      filters.showOnlyRefundable 
                        ? "bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-md" 
                        : "border-2 border-gray-200 hover:border-green-300 bg-white"
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <input
                      type="checkbox"
                      checked={filters.showOnlyRefundable}
                      onChange={(e) => handleFilterChange("showOnlyRefundable", e.target.checked)}
                      className="rounded text-green-600 focus:ring-green-500"
                    />
                    <span className={`text-sm font-medium ${filters.showOnlyRefundable ? "text-white" : "text-gray-700"}`}>
                      Refundable Only
                    </span>
                  </motion.label>
                  <motion.label 
                    className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg cursor-pointer transition-all duration-200 ${
                      filters.showOnlyWithMeals 
                        ? "bg-gradient-to-r from-purple-500 to-pink-600 text-white shadow-md" 
                        : "border-2 border-gray-200 hover:border-purple-300 bg-white"
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <input
                      type="checkbox"
                      checked={filters.showOnlyWithMeals}
                      onChange={(e) => handleFilterChange("showOnlyWithMeals", e.target.checked)}
                      className="rounded text-purple-600 focus:ring-purple-500"
                    />
                    <span className={`text-sm font-medium ${filters.showOnlyWithMeals ? "text-white" : "text-gray-700"}`}>
                      With Free Meals
                    </span>
                  </motion.label>
                </div>
              </motion.div>

              {/* Reset Button */}
              {activeFiltersCount > 0 && (
                <motion.div 
                  className="pt-4 border-t-2 border-gray-200 flex justify-end"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.7 }}
                >
                  <motion.button
                    onClick={resetFilters}
                    className="px-6 py-2.5 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-lg hover:from-red-600 hover:to-pink-600 transition-all duration-200 font-semibold flex items-center space-x-2 shadow-md"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FaUndo />
                    <span>Reset All Filters</span>
                  </motion.button>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdvancedFlightFilters;
